import React, { useState, useEffect, useCallback } from 'react';
import {
  Users,
  RotateCcw,
  Play,
  Pause,
  X,
  Clipboard,
  CheckCircle
} from 'lucide-react';
import ToolDescription from './ToolDescription';
import * as TeamService from '../services/teamService';
import timerService from '../services/timerService';

interface PairSession {
  driver: string;
  navigator: string;
  startTime: Date;
  notes: string[];
}

type PairType = [string, string] | [string, string, string];

const XPPractices: React.FC = () => {
  const [teamMembers, setTeamMembers] = useState<string[]>(TeamService.getTeamMembers());
  const [newMember, setNewMember] = useState('');
  
  // Initialize current pairs from localStorage or empty array
  const [currentPairs, setCurrentPairs] = useState<PairType[]>(() => {
    const savedPairs = localStorage.getItem('currentPairs');
    if (savedPairs) {
      try {
        return JSON.parse(savedPairs);
      } catch (error) {
        console.error('Failed to parse saved pairs:', error);
        return [];
      }
    }
    return [];
  });
  
  // Usamos el estado del timer global
  const [pairTimer, setPairTimer] = useState(timerService.getTimerState());
  
  // Initialize sessions from localStorage or empty array
  const [sessions, setSessions] = useState<PairSession[]>(() => {
    const savedSessions = localStorage.getItem('pairSessions');
    if (savedSessions) {
      try {
        // Parse and convert date strings back to Date objects
        const parsedSessions = JSON.parse(savedSessions) as Array<Omit<PairSession, 'startTime'> & { startTime: string }>;
        return parsedSessions.map(session => ({
          ...session,
          startTime: new Date(session.startTime)
        }));
      } catch (error) {
        console.error('Failed to parse saved sessions:', error);
        return [];
      }
    }
    return [];
  });
  
  const [currentNote, setCurrentNote] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  // Save current pairs to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('currentPairs', JSON.stringify(currentPairs));
  }, [currentPairs]);

  // Ya no necesitamos guardar el timer en localStorage, el servicio lo hace
  // useEffect(() => {
  //   localStorage.setItem('pairTimer', JSON.stringify(pairTimer));
  // }, [pairTimer]);

  // Save sessions to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('pairSessions', JSON.stringify(sessions));
  }, [sessions]);

  // Suscribirse al timer service para recibir actualizaciones
  useEffect(() => {
    // La función subscribe devuelve una función para desuscribirse
    const unsubscribe = timerService.subscribe((timerState) => {
      setPairTimer(timerState);
    });
    
    // Limpiar la suscripción cuando el componente se desmonte
    return unsubscribe;
  }, []);
  
  useEffect(() => {
    const handleTeamMembersUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail && customEvent.detail.members) {
        setTeamMembers(customEvent.detail.members);
      }
    };
    
    window.addEventListener('team-members-updated', handleTeamMembersUpdate);
    
    return () => {
      window.removeEventListener('team-members-updated', handleTeamMembersUpdate);
    };
  }, []);

  const handleAddTeamMember = (e: React.FormEvent) => {
    e.preventDefault();
    if (newMember.trim() && !teamMembers.includes(newMember.trim())) {
      const updatedMembers = TeamService.addTeamMember(newMember.trim());
      setTeamMembers(updatedMembers);
      setNewMember('');
    }
  };

  const handleRemoveTeamMember = (member: string) => {
    const updatedMembers = TeamService.removeTeamMember(member);
    setTeamMembers(updatedMembers);
  };

  const generatePairs = () => {
    const shuffled = [...teamMembers].sort(() => Math.random() - 0.5);
    const pairs: PairType[] = [];
    
    for (let i = 0; i < shuffled.length - 1; i += 2) {
      pairs.push([shuffled[i], shuffled[i + 1]]);
    }
    
    if (shuffled.length % 2 !== 0) {
      // Add the last person to the last pair to form a trio
      const lastPerson = shuffled[shuffled.length - 1];
      if (pairs.length > 0) {
        const lastPair = pairs[pairs.length - 1];
        pairs[pairs.length - 1] = [lastPair[0], lastPair[1], lastPerson] as [string, string, string];
      } else {
        pairs.push([lastPerson, 'Needs a pair']);
      }
    }
    
    setCurrentPairs(pairs);
    resetPairTimer();
  };

  // Usamos el servicio de timer global
  const togglePairTimer = useCallback(() => {
    timerService.toggleTimer();
  }, []);

  const resetPairTimer = useCallback(() => {
    timerService.resetTimer();
  }, []);

  const updateTimerDuration = useCallback((minutes: number) => {
    timerService.updateTimerDuration(minutes);
  }, []);

  const formatTime = useCallback((seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }, []);

  const addNote = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    
    if (currentNote.trim()) {
      const trimmedNote = currentNote.trim();
      
      setSessions((prevSessions: PairSession[]) => {
        // Check if there's a session from today
        const now = new Date();
        const today = now.toDateString();
        
        // Si hay pares actuales, usamos esa información
        if (currentPairs.length > 0) {
          const existingSessionIndex = prevSessions.findIndex(
            session => session.startTime.toDateString() === today &&
                      session.driver === currentPairs[0][0] &&
                      session.navigator === currentPairs[0][1]
          );
          
          if (existingSessionIndex >= 0) {
            // Add note to existing session
            const updatedSessions = [...prevSessions];
            updatedSessions[existingSessionIndex] = {
              ...updatedSessions[existingSessionIndex],
              notes: [...updatedSessions[existingSessionIndex].notes, trimmedNote]
            };
            return updatedSessions;
          } else {
            // Create new session with pair info
            const newSession: PairSession = {
              driver: currentPairs[0][0],
              navigator: currentPairs[0][1],
              startTime: now,
              notes: [trimmedNote]
            };
            return [...prevSessions, newSession];
          }
        } else {
          // Si no hay pares, creamos una sesión solo con notas
          const existingSessionIndex = prevSessions.findIndex(
            session => session.startTime.toDateString() === today &&
                      session.driver === "Unknown" &&
                      session.navigator === "Unknown"
          );
          
          if (existingSessionIndex >= 0) {
            // Add note to existing session without pairs
            const updatedSessions = [...prevSessions];
            updatedSessions[existingSessionIndex] = {
              ...updatedSessions[existingSessionIndex],
              notes: [...updatedSessions[existingSessionIndex].notes, trimmedNote]
            };
            return updatedSessions;
          } else {
            // Create new session without pair info
            const newSession: PairSession = {
              driver: "Unknown",
              navigator: "Unknown",
              startTime: now,
              notes: [trimmedNote]
            };
            return [...prevSessions, newSession];
          }
        }
      });
      
      setCurrentNote('');
    }
  }, [currentNote, currentPairs]);

  // Add this function to clear all session notes
  const clearSessionNotes = () => {
    setSessions([]);
  };

  // Función para copiar todas las notas al clipboard en formato markdown
  const copyNotesToClipboard = useCallback(() => {
    if (sessions.length === 0) return;

    let markdownContent = "# Pair Programming Session Notes\n\n";

    sessions.forEach((session, index) => {
      const formattedDate = session.startTime.toLocaleString();
      markdownContent += `## Session ${index + 1}: ${formattedDate}\n`;
      
      if (session.driver !== "Unknown") {
        markdownContent += `**Driver:** ${session.driver}\n`;
        markdownContent += `**Navigator:** ${session.navigator}\n`;
      }
      
      markdownContent += "\n### Notes:\n";
      session.notes.forEach(note => {
        markdownContent += `- ${note}\n`;
      });
      markdownContent += "\n";
    });

    navigator.clipboard.writeText(markdownContent)
      .then(() => {
        setCopySuccess(true);
        // Reset success message after 2 seconds
        setTimeout(() => setCopySuccess(false), 2000);
      })
      .catch(err => {
        console.error('Error al copiar al portapapeles: ', err);
      });
  }, [sessions]);

  return (
    <div className="space-y-6 p-4">
      <ToolDescription 
        title="Pair Programming"
        description="An essential Extreme Programming (XP) practice that involves two developers working together on the same code. This tool helps you organize pair programming sessions, manage team rotations, and track session progress with built-in timers."
        benefits={[
          "Improve code quality through real-time code review",
          "Share knowledge and reduce knowledge silos",
          "Catch bugs and issues early in development",
          "Mentor junior developers effectively",
          "Increase team collaboration and communication"
        ]}
        howToUse="Add your team members, then use the rotation feature to generate pairs. Start a session with the built-in timer to track your pair programming intervals. The tool automatically suggests rotations to ensure everyone pairs with everyone."
      />

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2 mb-6">
          <Users size={24} />
          Pair Programming
        </h2>

        {/* Team Members Management */}
        <div className="flex flex-col items-center gap-6">
          <form onSubmit={handleAddTeamMember} className="flex gap-2 w-full max-w-md">
            <input
              type="text"
              value={newMember}
              onChange={(e) => setNewMember(e.target.value)}
              placeholder="Add team member"
              className="flex-1 px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600"
            />
            <button
              type="submit"
              className="flex-1 p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center justify-center gap-2"
            >
              Add
            </button>
          </form>

          {/* Team Members List */}
          <div className="flex flex-wrap gap-2 justify-center">
            {teamMembers.map((member) => (
              <div
                key={member}
                className="p-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg"
              >
                <span>{member}</span>
                <button
                  onClick={() => handleRemoveTeamMember(member)}
                  className="text-red-500 hover:text-red-600"
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          {/* Generate Pairs Button */}
          <button
            onClick={generatePairs}
            className="flex-1 py-3 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center justify-center gap-2 max-w-md"
            disabled={teamMembers.length < 2}
          >
            Generate Pairs
          </button>
        </div>

        {/* Current Pairs Display */}
        {currentPairs.length > 0 && (
          <div className="space-y-4 mt-8">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold text-gray-800 dark:text-white text-lg">Current Pairs</h3>
              <button
                onClick={() => setCurrentPairs([])}
                className="text-red-500 hover:text-red-600 text-sm flex items-center gap-1 px-2 py-1 rounded-lg border border-red-200 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/30"
              >
                <X size={14} />
                Clear
              </button>
            </div>
            <div className="grid gap-4">
              {currentPairs.map((pair, index) => (
                <div
                  key={index}
                  className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg"
                >
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center">
                      <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded text-xs font-medium mr-2">Driver</span>
                      <span className="font-medium">{pair[0]}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded text-xs font-medium mr-2">Navigator</span>
                      <span className="font-medium">{pair[1]}</span>
                    </div>
                    {pair[2] && (
                      <div className="flex items-center">
                        <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 px-2 py-0.5 rounded text-xs font-medium mr-2">Observer</span>
                        <span className="font-medium">{pair[2]}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pair Timer */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-4 mt-8">
          <div className="text-center space-y-4">
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
              {formatTime(pairTimer.timeLeft)}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Time until next switch
            </div>
            <div className="flex justify-center gap-2">
              {[5, 10, 15, 25, 30].map(minutes => (
                <button
                  key={minutes}
                  onClick={() => updateTimerDuration(minutes)}
                  className={`px-3 py-1 rounded-lg text-sm ${
                    pairTimer.duration === minutes
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                  }`}
                >
                  {minutes}m
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-center gap-2">
            <button
              onClick={togglePairTimer}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                pairTimer.isRunning
                  ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                  : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
              }`}
            >
              {pairTimer.isRunning ? <Pause size={20} /> : <Play size={20} />}
              <span>{pairTimer.isRunning ? 'Pause' : 'Start'}</span>
            </button>
            <button
              onClick={resetPairTimer}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300"
            >
              <RotateCcw size={20} />
              <span>Reset</span>
            </button>
          </div>
        </div>

        {/* Session Notes */}
        <div className="space-y-4 mt-8">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-gray-800 dark:text-white text-lg">Session Notes</h3>
            <div className="flex gap-2">
              {sessions.length > 0 && (
                <>
                  <button
                    onClick={copyNotesToClipboard}
                    className="text-blue-500 hover:text-blue-600 text-sm flex items-center gap-1 px-2 py-1 rounded-lg border border-blue-200 hover:bg-blue-50 dark:border-blue-800 dark:hover:bg-blue-900/30"
                    title="Copy all notes in markdown format"
                  >
                    {copySuccess ? (
                      <>
                        <CheckCircle size={14} />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <Clipboard size={14} />
                        <span>Copy to MD</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={clearSessionNotes}
                    className="text-red-500 hover:text-red-600 text-sm flex items-center gap-1 px-2 py-1 rounded-lg border border-red-200 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/30"
                  >
                    <X size={14} />
                    <span>Clear All</span>
                  </button>
                </>
              )}
            </div>
          </div>
          <form onSubmit={addNote} className="flex gap-2 mb-4">
            <input
              type="text"
              value={currentNote}
              onChange={(e) => setCurrentNote(e.target.value)}
              placeholder="Add a note about your session"
              className="flex-1 px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600"
            />
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              disabled={!currentNote.trim()}
            >
              Add Note
            </button>
          </form>
          
          {/* Session list */}
          <div className="space-y-2">
            {sessions.length > 0 ? (
              sessions.map((session, index) => (
                <div key={index} className="bg-blue-50 dark:bg-gray-700 p-4 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {session.startTime.toLocaleString()}
                    </div>
                    {(session.driver !== "Unknown") && (
                      <div className="text-xs bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded">
                        <span className="font-medium">Driver:</span> {session.driver}, <span className="font-medium">Navigator:</span> {session.navigator}
                      </div>
                    )}
                  </div>
                  <ul className="list-disc list-inside mt-2">
                    {session.notes.map((note, noteIndex) => (
                      <li key={noteIndex} className="text-gray-800 dark:text-gray-200">
                        {note}
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            ) : (
              <div className="text-gray-500 dark:text-gray-400 italic p-4 text-center">
                No session notes yet. Add some notes about your session!
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default XPPractices; 